/*
 * PDP delivery-date estimate (seed-036).
 * Computes a localized "Delivery on <range>" client-side each load, so the
 * cached PDP never shows a stale date. Skips weekends + configured holidays.
 * See docs/superpowers/specs/2026-07-28-pdp-delivery-estimate-design.md
 */
(function () {
  'use strict';

  function toLocalISO(d) {
    var y = d.getFullYear();
    var m = String(d.getMonth() + 1).padStart(2, '0');
    var day = String(d.getDate()).padStart(2, '0');
    return y + '-' + m + '-' + day;
  }

  function parseHolidays(raw) {
    var set = new Set();
    if (!raw) return set;
    raw.split(/[\s,;]+/).forEach(function (tok) {
      var t = tok.trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(t)) set.add(t);
    });
    return set;
  }

  function isBusinessDay(d, holidays) {
    var wd = d.getDay(); // 0 Sun .. 6 Sat
    if (wd === 0 || wd === 6) return false;
    return !holidays.has(toLocalISO(d));
  }

  // Delivery date = `n` business days after dispatch. Dispatch day 0 is today
  // when today is a business day, otherwise the next business day (no cutoff).
  // So an order placed on a weekend dispatches Monday, then counts n forward.
  function addBusinessDays(start, n, holidays) {
    var d = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    while (!isBusinessDay(d, holidays)) d.setDate(d.getDate() + 1); // roll to dispatch day
    var added = 0;
    while (added < n) {
      d.setDate(d.getDate() + 1);
      if (isBusinessDay(d, holidays)) added++;
    }
    return d;
  }

  var DeliveryEstimate = {
    init: function (el) {
      if (!el || el.__pjInit) return;
      el.__pjInit = true;

      var minDays = parseInt(el.getAttribute('data-min-days'), 10);
      var maxDays = parseInt(el.getAttribute('data-max-days'), 10);
      if (isNaN(minDays)) minDays = maxDays;
      if (isNaN(maxDays)) return;

      var holidays = parseHolidays(el.getAttribute('data-holidays'));
      var locale = el.getAttribute('data-locale') || 'en';
      var freeEnabled = el.getAttribute('data-free-enabled') === '1';
      var thresholdCents = parseInt(el.getAttribute('data-threshold-cents'), 10) || 0;
      var section = el.getAttribute('data-section');

      // Compose "Weekday, DD Mon" from parts so every locale keeps the same
      // order + abbreviated month (ICU's default layout differs per locale).
      function makeFmt(opt) {
        try { return new Intl.DateTimeFormat(locale, opt); }
        catch (e) { return new Intl.DateTimeFormat('en', opt); }
      }
      var fmtWeekday = makeFmt({ weekday: 'short' });
      var fmtDay = makeFmt({ day: 'numeric' });
      var fmtMonth = makeFmt({ month: 'short' });
      function formatDate(d) {
        return fmtWeekday.format(d).replace(/\.$/, '') + ', ' + fmtDay.format(d) + ' ' + fmtMonth.format(d);
      }

      // Variant price/availability map emitted by the snippet.
      var variants = [];
      try {
        var mapEl = document.querySelector('[data-pj-delivery-variants="' + section + '"]');
        if (mapEl) variants = JSON.parse(mapEl.textContent);
      } catch (e) { /* leave empty */ }

      var textEl = el.querySelector('[data-pj-delivery-text]');
      var productInfo = el.closest('product-info') || document;

      function currentVariant() {
        var id = null;
        var input = productInfo.querySelector('input[name="id"], select[name="id"]');
        if (input && input.value) id = parseInt(input.value, 10);
        if (!id) id = parseInt(el.getAttribute('data-selected-id'), 10);
        for (var i = 0; i < variants.length; i++) {
          if (variants[i].id === id) return variants[i];
        }
        return variants.length ? variants[0] : null;
      }

      function render() {
        var v = currentVariant();
        // Hide when the chosen variant is unavailable / not found.
        if (v && v.available === false) { el.hidden = true; return; }

        var today = new Date();
        var minDate = addBusinessDays(today, minDays, holidays);
        var maxDate = addBusinessDays(today, maxDays, holidays);
        var minStr = formatDate(minDate);
        var maxStr = formatDate(maxDate);

        var isFree = freeEnabled && v && typeof v.price === 'number' && v.price >= thresholdCents;
        var label = isFree ? el.getAttribute('data-label-free') : el.getAttribute('data-label-delivery');
        var sep = el.getAttribute('data-separator') || ' - ';

        var range = (minStr === maxStr) ? minStr : (minStr + sep + maxStr);
        if (textEl) textEl.textContent = (label ? label + ' ' : '') + range;
        el.hidden = false;
      }

      render();

      // React to variant changes: Dawn re-renders price/inventory nodes inside
      // product-info, which fire subtree mutations after the hidden id updates.
      if (productInfo && productInfo !== document && 'MutationObserver' in window) {
        var t;
        var obs = new MutationObserver(function () {
          clearTimeout(t);
          t = setTimeout(render, 60);
        });
        obs.observe(productInfo, { childList: true, subtree: true });
      }
      // Belt-and-suspenders: also recompute on any variant-select change.
      productInfo.addEventListener('change', function (e) {
        if (e.target && e.target.name === 'id') setTimeout(render, 0);
      });
    },

    scan: function () {
      document.querySelectorAll('[data-pj-delivery]').forEach(DeliveryEstimate.init);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', DeliveryEstimate.scan);
  } else {
    DeliveryEstimate.scan();
  }
  // Re-scan after Shopify section re-render in the theme editor.
  document.addEventListener('shopify:section:load', DeliveryEstimate.scan);
})();
